/**
 * @file     make-signature.js - signature creation code for PrivateKey and Keystore classes. Based on
 *           keystore.js 337995e6e30c743876149e26433a2b17d1e0f059.
 * @author   Nazila Akhavan <nazila@distributive.network>
 * @author   Wes Garland <wes@distributive.network>
 * @date     Aug 2024, May 2020
 */
'use strict';

const dcpEnv = require('dcp/common/dcp-env');
const debugging = require('dcp/debugging').scope('wallet/keystore');

let useWasm = true;
let secp256k1 = null;

/* Place canonical versions of ethereumjs-util and ethereumjs-wallet on
 * exports._internalEth for reference by 'friend' modules.  Try to load 
 * native (fast) versions on NodeJS, but fallback to internal modules
 * when not present. Modules are argumented with fast keccak
 * hashing code via wasm for internal use.
 */
exports._internalEth = {};

if (dcpEnv.platform === 'nodejs')
{
  /* Prefer peer dependencies over webpacked versions whenever possible, 
   * because they will have native crypto via scrypt.
   */
  const { requireNative } = require('dcp/dcp-client/webpack-native-bridge');
  try
  {
    exports._internalEth.util   = requireNative('ethereumjs-util');
    exports._internalEth.wallet = requireNative('ethereumjs-wallet').default;
    exports._internalEth.buffer = requireNative('buffer');
    exports._internalEth.impl   = 'native';
    useWasm = false;  /* only disable wasm when using node AND have native util, wallet available */
  }
  catch(e)
  {
    debugging() && console.log('Could not initialize native ethereumjs libraries', e);
  }
}

if (exports._internalEth.impl !== 'native')
{
  exports._internalEth.util   = require('ethereumjs-util');
  exports._internalEth.wallet = require('ethereumjs-wallet').default;
  exports._internalEth.buffer = require('buffer');
  exports._internalEth.impl   = 'default';
}

debugging() && console.log(`using ${exports._internalEth.impl} bindings for ethereumjs`);

const ethUtil   = exports._internalEth.util;
const Buffer    = exports._internalEth.buffer.Buffer;
const fromPrivateKey = exports._internalEth.wallet;

/**
 * Creates and returns stringified signedMessage in the format:
 *       JSON.stringify({ owner: address, signature: signature, body: messageBody })
 * @async
 * @access public
 * @param {object} messageBody
 * @param {object} privateKey
 * @returns {Promise<string>}
 */
exports.makeSignedMessage = async function makeSignedMessage(messageBody, privateKey)
{
  const address = getAddressFromPrivateKey(privateKey);
  const signature = await exports.makeSignature(messageBody, privateKey);
  return JSON.stringify({ owner: address, signature: signature, body: messageBody });
}

/** @typedef {object} Signature
 * @property {Uint8Array} r
 * @property {Uint8Array} s
 * @property {Uint8Array} v
 */

/**
 * Creates and returns signature for messageBody
 *       { owner: address, signature: signature, auth: messageLightWeight, body: messageBody };
 * The signature is computed only on messageLightWeight, in order to improve perf when messageBody is large.
 * @async
 * @access public
 * @param {object} messageBody
 * @param {object} privateKey
 * @returns {Promise<Signature>}
 */
exports.makeSignature = async function makeSignature(messageBody, privateKey)
{
  if (typeof messageBody !== 'string') {
    messageBody = JSON.stringify(messageBody);
  }

  if (useWasm && !secp256k1) {
    const { instantiateSecp256k1 } = await import('@bitauth/libauth');  
    secp256k1 = await instantiateSecp256k1();
    debugging() && console.log('initialized libauth secp256k1 code');
  } 
  const signFn = useWasm ? exports.ecsign : ethUtil.ecsign;
  const signature = signFn(ethUtil.hashPersonalMessage(Buffer.from(messageBody)), ethUtil.toBuffer(privateKey.toString()));

  return signature;
}

/**
 * Get the public address of the wallet from its privateKey
 *
 * @param {object} privateKey 
 * @returns {Address}
 */
const getAddressFromPrivateKey = function(privateKey)
{
  const dcpEth = require('./eth');
  const wallet = new fromPrivateKey(Buffer.from(String(privateKey).slice(2), 'hex'));
  return new dcpEth.Address('0x' + wallet.getAddress().toString('hex'));
}

exports.ecsign = async function (msgHash, privateKey)
{
  var sig = secp256k1.signMessageHashRecoverableCompact(privateKey, msgHash);
  sig.signature = Buffer.from(sig.signature, 'Uint8Array');

  var ret = {};
  ret.r = sig.signature.slice(0, 32);
  ret.s = sig.signature.slice(32, 64);
  ret.v = sig.recoveryId + 27;

  return ret;
};
